import React, { forwardRef, PropsWithChildren, MouseEvent, useRef, Suspense, FC } from 'react';
import { Episode } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { ErrorBoundary } from '@funtech-int/rival-react';

import { withVisibilityFade } from '@/shared/components/Carousel/withVisibilityFade';
import { useSeasonWithEpisodesQuery } from '@/shared/hooks/useSeasonWithEpisodesQuery';
import { ScrollSentinel } from '@/shared/components/ScrollSentinel/ScrollSentinel';

import { filmRoute } from '../../routes';

import { EpisodeSnippet } from './_EpisodeSnippet';
import styles from './_EpisodesList.module.css';

const SKELETON_EPISODE_COUNT = 4;

const EpisodesListSkeleton = () => (
    <ul className={styles.episodesList} aria-hidden>
        {Array.from({ length: SKELETON_EPISODE_COUNT }, (_, index) => (
            <li key={index} className={styles.episodeSnippetSkeleton}>
                <div className={styles.skeletonImage} />
            </li>
        ))}
    </ul>
);

interface DefaultCardWrapperProps {
    className?: string;
}

const SnippetFadeWrapper = withVisibilityFade(
    forwardRef<HTMLLIElement, PropsWithChildren<DefaultCardWrapperProps>>(
        ({ children, className }, ref) => (
            <li ref={ref} className={className}>
                {children}
            </li>
        )
    )
);

interface EpisodesListComponentProps {
    seasonId: string;
    filmId: string;
    onEpisodeClick: (episode: Episode) => void;
    playingContentId: string;
    currentEpisodeTiming: number;
}

const EpisodesListComponent: FC<EpisodesListComponentProps> = ({
    seasonId,
    filmId,
    playingContentId,
    currentEpisodeTiming,
    onEpisodeClick,
}) => {
    const scrollRef = useRef<HTMLUListElement>(null);

    const { season, pagination, hasMore } = useSeasonWithEpisodesQuery({
        id: seasonId,
        withUserData: true,
    });

    return (
        <ul className={styles.episodesList} ref={scrollRef}>
            {season?.episodes.items.map((episode: Episode) => {
                const handleClick = (event: MouseEvent<HTMLDivElement>) => {
                    event.preventDefault();
                    onEpisodeClick(episode);
                };

                const currentEpisodeWatchingProgress =
                    playingContentId === episode.contentId ? currentEpisodeTiming : undefined;

                return (
                    <SnippetFadeWrapper key={episode.contentId} scrollRef={scrollRef}>
                        <EpisodeSnippet
                            episode={episode}
                            currentEpisodeWatchingProgress={currentEpisodeWatchingProgress}
                            location={filmRoute({ id: filmId })}
                            onClick={handleClick}
                            isSelected={playingContentId === episode.contentId}
                        />
                    </SnippetFadeWrapper>
                );
            })}
            {hasMore && (
                <ScrollSentinel
                    key={seasonId + (season?.episodes.items.length ?? 0).toString()}
                    direction="end"
                    orientation="vertical"
                    pagination={pagination}
                    skeleton={<EpisodesListSkeleton />}
                />
            )}
        </ul>
    );
};

export const EpisodesList = ({
    seasonId,
    filmId,
    onEpisodeClick,
    currentEpisodeTiming,
    playingContentId,
}: EpisodesListComponentProps) => (
    <ErrorBoundary fallback={() => null}>
        <Suspense fallback={<EpisodesListSkeleton />}>
            <EpisodesListComponent
                seasonId={seasonId}
                filmId={filmId}
                onEpisodeClick={onEpisodeClick}
                currentEpisodeTiming={currentEpisodeTiming}
                playingContentId={playingContentId}
            />
        </Suspense>
    </ErrorBoundary>
);
